import React, { FC, useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';

import { AlertDialogWrapper } from '../../../../components/alert-dialog/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { CloseModalButton } from '../../../../components/close-modal-button/index';
import { FormDatePicker } from '../../../../components/date-picker/index';
import { DropDown } from '../../../../components/dropdown/index';
import { Sheet, SheetContent } from '../../../../components/sheet/index';
import { StorageDocumentLink } from '../../../../components/storage-document-link/index';
import { SummaryItem } from '../../../../components/summary-list/index';
import { Pool, useFormat, useModalState } from '../../../../hooks/index';
import { DilutionCondition } from '../../../../types/pool-plans.types';
import { openCompanyStorageUrlInNewTab } from '../../../../utils/company-storage-url.util';
import { getS3FileOriginalName } from '../../../../utils/get-s3file-original-name.util';
import { UpdateConfirmationModal } from '../../update-confirmation-modal';

type ModalProps = {
  companyId: string;
  handleClose: () => void;
  handleTerminate: (state: { date: Date; id: string }) => void;
  invalidateQuery: () => void;
  isOpenModal: boolean;
  poolId: string;
};

export const PoolTerminationDateModal: FC<ModalProps> = ({
  companyId,
  isOpenModal,
  handleClose,
  invalidateQuery,
  poolId,
  handleTerminate,
}) => {
  const { pool } = Pool.usePool({ companyId, poolId });
  const { format } = useFormat();
  const [terminatingDate, setTerminatingDate] = useState<Date | null>(null);
  const [filesLinks, setFilesLinks] = useState<
    | {
        abort: () => void;
        doc: {
          name: string;
          size: number;
          type: string;
        };
        docLink: string;
        id: string;
        loadProgress: 100;
      }[]
    | null
  >(null);

  useEffect(() => {
    if (!pool) return;

    const files: {
      abort: () => void;
      doc: {
        name: string;
        size: number;
        type: string;
      };
      docLink: string;
      id: string;
      loadProgress: 100;
    }[] = pool?.filesLinks?.map((link) => {
      const originalFileName = getS3FileOriginalName(link);

      return {
        abort: () => '',
        loadProgress: 100,
        doc: {
          name: originalFileName || '',
          size: 10,
          type: 'application/pdf',
        },
        docLink: link,
        id: uuidv4(),
      };
    });
    setFilesLinks(files);
  }, [pool]);

  const handleCloseModal = useCallback(() => {
    handleClose();
    invalidateQuery();
    setTerminatingDate(null);
  }, [handleClose, invalidateQuery]);

  const {
    toggler: confirmationModalToggler,
    isOpen: isOpenConfirmationModal,
    handleOpenModal: handleOpenConfirmationModal,
    handleCloseModal: handleCloseConfirmationModal,
    handleSuccessModal: handleSuccessConfirmationModal,
  } = useModalState({
    onSuccess: () => {
      if (terminatingDate) {
        handleTerminate({ id: poolId, date: terminatingDate });
      }
      handleCloseModal();
      toast.success(<AppFormattedMessage id="TERMINATION_DATE_UPDATED" />);
    },
  });

  const handleDocumentClick = (link: string) => {
    void openCompanyStorageUrlInNewTab(companyId, link);
  };

  const dilutionCondition = pool?.capTableInclusion
    ? DilutionCondition.INCLUDE_CAP_TABLE
    : DilutionCondition.EXCLUDE_CAP_TABLE;

  return (
    <Sheet onOpenChange={(open) => !open && handleCloseModal()} open={isOpenModal}>
      <SheetContent
        className="w-full max-w-[485px] border-transparent bg-transparent p-2 shadow-none"
        onInteractOutside={handleCloseModal}
        side="RIGHT"
      >
        <AlertDialogWrapper
          control={{ onOpenChange: confirmationModalToggler, open: isOpenConfirmationModal }}
        >
          <UpdateConfirmationModal
            onClose={handleCloseConfirmationModal}
            onSuccess={handleSuccessConfirmationModal}
            text="pool"
          />
        </AlertDialogWrapper>

        <div className="flex h-full w-full flex-col overflow-hidden rounded-lg border-[1px] border-gray-300 bg-gray-100 pb-4">
          <div className="flex h-fit w-full items-center justify-between bg-white px-6 py-3">
            <span className="truncate text-xl font-[550] text-gray-700">
              <AppFormattedMessage id="EDIT_TERMINATION_DATE" />
            </span>
            <CloseModalButton onClose={handleCloseModal} />
          </div>
          <div className="flex h-full flex-col gap-4 overflow-hidden">
            {pool && (
              <div className="flex h-full flex-col gap-4 overflow-y-auto rounded-b-xl border-b border-gray-300 bg-white p-6 pt-0">
                <div className="flex h-fit flex-col gap-2 rounded-lg bg-gray-50 pt-2">
                  <div className="flex w-full justify-between px-4">
                    <span className="text-sm font-[550] text-gray-700">
                      <AppFormattedMessage id="GENERAL" />
                    </span>
                  </div>
                  <div className="flex flex-col divide-y divide-gray-100 rounded-b-lg border border-gray-100 bg-white p-4 pb-3">
                    <SummaryItem
                      className="pt-0"
                      title={<AppFormattedMessage id="POOL_NAME" />}
                      value={pool?.name}
                    />

                    <SummaryItem
                      className="py-3"
                      title={<AppFormattedMessage id="FULLY_DILUTED_SHARES" />}
                      value={pool?.dillutedSharesCount}
                    />
                    <SummaryItem
                      className="py-3"
                      title={<AppFormattedMessage id="SHARE_CLASS" />}
                      value={pool?.shareClass?.name || '-'}
                    />
                    <SummaryItem
                      className="py-3"
                      title={<AppFormattedMessage id="DATE" />}
                      value={pool?.date ? format(pool?.date, 'dd/MM/yyyy') : '-'}
                    />
                    <SummaryItem
                      className="pb-0"
                      title={<AppFormattedMessage id="DILUTION_CONDITION" />}
                      value={
                        dilutionCondition === DilutionCondition.INCLUDE_CAP_TABLE ? (
                          <AppFormattedMessage id="INCLUDE_CAP_TABLE" />
                        ) : (
                          <AppFormattedMessage id="EXCLUDE_CAP_TABLE" />
                        )
                      }
                    />
                  </div>
                </div>
                <div className="flex w-full min-w-0 items-start justify-between overflow-hidden rounded-lg border border-gray-100 bg-white px-4">
                  <div className="flex min-w-0 flex-1 gap-3 overflow-hidden py-2">
                    <span className="w-[120px] shrink-0 text-xs font-[450] text-gray-500">
                      <AppFormattedMessage id="DOCUMENTS" />:
                    </span>
                    <div className="flex min-w-0 flex-1 flex-col gap-2 overflow-hidden">
                      {(filesLinks?.length || 0) > 0 ? (
                        filesLinks?.map(({ doc, docLink }) => (
                          <StorageDocumentLink
                            key={docLink}
                            label={doc?.name ?? getS3FileOriginalName(docLink) ?? docLink}
                            link={docLink}
                            onDocumentClick={handleDocumentClick}
                          />
                        ))
                      ) : (
                        <span className="truncate text-xs font-[450] text-gray-700">-</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex w-full items-center justify-between rounded-lg border border-gray-100 bg-white px-4 py-2">
                  <div className="flex w-full gap-3">
                    <span className="w-[120px] flex-shrink-0 text-xs font-[450] text-gray-500">
                      {<AppFormattedMessage id="ADDITIONAL_NOTES" />}:
                    </span>
                    <div className="max-w-[250px] break-words text-xs font-[450] text-gray-700">
                      {pool?.additionalNotes || '-'}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-3 p-3 pt-5">
                  <div className="flex flex-col gap-1">
                    <span className="text-sm font-[550] text-fireside-600">
                      <AppFormattedMessage id="TERMINATE_POOL" />
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-[450] text-gray-700">
                        <AppFormattedMessage id="CURRENT_TERMINATION_DATE" />:
                      </span>
                      <span className="text-xs font-[450] text-fireside-600">
                        {pool?.terminationDate ? format(pool.terminationDate, 'dd/MM/yy') : ''}
                      </span>
                    </div>
                  </div>

                  <div className="flex w-full flex-col gap-3 rounded-md bg-white p-3 shadow-xs">
                    <FormDatePicker
                      calendar={{ fromDate: new Date() }}
                      inputValue={terminatingDate ? format(terminatingDate, 'dd/MM/yy') : ''}
                      inputWrapperClassName="cursor-pointer"
                      onSelect={(date) => setTerminatingDate(date)}
                      placeholder={<AppFormattedMessage id="NEW_TERMINATION_DATE" />}
                      value={terminatingDate}
                    />
                    <DropDown
                      answer="Pool termination refers to the process of ending an employee stock option plan, stock purchase plan, or any other equity compensation plan."
                      className="rounded-lg bg-gray-50"
                      question={<AppFormattedMessage id="POOL_TERMINATION_WORKS" />}
                    />
                  </div>
                </div>
              </div>
            )}
            <div className="flex h-9 w-full justify-between gap-3 px-6">
              <Button
                className="w-fit bg-transparent px-4 py-[6px] text-sm font-[450] text-gray-700"
                onClick={handleCloseModal}
                styleType="NONE"
              >
                <AppFormattedMessage id="CANCEL" />
              </Button>
              <div className="flex gap-3">
                <Button
                  className="h-full w-fit bg-fireside-600 px-4 py-[6px] text-sm font-[550] text-white"
                  disabled={!terminatingDate}
                  onClick={handleOpenConfirmationModal}
                  type="button"
                >
                  <AppFormattedMessage id="UPDATE" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
